/**
 * Multi-File PDF Generator
 *
 * Generates multiple separate PDF files (one per branch) and packages them as a ZIP
 * for enterprise customers preferring individual branch statements.
 *
 * Phase 3C - Consolidated Statement PDF Template (Multiple Files Format)
 * User Story: US-005 (Invoice Generation Enhancement)
 */

import { pdf } from '@react-pdf/renderer'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import BranchStatementTemplate from '../components/invoice/BranchStatementTemplate'

/**
 * Generate multiple PDF files, one for each branch, and download as ZIP
 *
 * @param {Object} statementData - Consolidated statement data
 * @param {Object} farmInfo - Farm information for PDF header
 * @param {string} customerName - Customer name for file naming
 * @returns {Promise<void>}
 */
export const generateMultiFilePDFs = async (statementData, farmInfo, customerName) => {
  try {
    const zip = new JSZip()
    const pdfPromises = []

    // Create safe filename from customer name
    const safeCustomerName = customerName.replace(/[^a-zA-Z0-9]/g, '_')
    const startDate = statementData.startDate.replace(/-/g, '')
    const endDate = statementData.endDate.replace(/-/g, '')

    // Generate a PDF for each branch
    for (const [branchId, branchData] of Object.entries(statementData.branchGroups)) {
      const branchName = branchData.branchName.replace(/[^a-zA-Z0-9]/g, '_')
      const costCenter = branchData.costCenterCode || 'NO_CC'

      // Create individual branch statement data
      const branchStatementData = {
        customerInfo: statementData.customerInfo,
        branch: branchData,
        startDate: statementData.startDate,
        endDate: statementData.endDate,
        generatedAt: statementData.generatedAt
      }

      // Calculate branch totals
      const branchInvoiced = branchData.invoices.reduce((sum, inv) => sum + inv.total, 0)
      const branchPaid = branchData.payments.reduce((sum, pay) => sum + pay.amount, 0)
      const branchOutstanding = branchData.invoices
        .filter(inv => !inv.paid)
        .reduce((sum, inv) => sum + inv.total, 0)

      branchStatementData.totals = {
        totalInvoiced: parseFloat(branchInvoiced.toFixed(2)),
        totalPaid: parseFloat(branchPaid.toFixed(2)),
        totalOutstanding: parseFloat(branchOutstanding.toFixed(2)),
        currentBalance: parseFloat((branchPaid - branchOutstanding).toFixed(2))
      }

      // Generate PDF blob for this branch
      const pdfPromise = pdf(
        <BranchStatementTemplate
          statementData={branchStatementData}
          farmInfo={farmInfo}
        />
      ).toBlob().then(blob => {
        // Add to ZIP with descriptive filename
        const filename = `${safeCustomerName}_${branchName}_${costCenter}_${startDate}_to_${endDate}.pdf`
        zip.file(filename, blob)
        return filename
      })

      pdfPromises.push(pdfPromise)
    }

    // Wait for all PDFs to be generated
    const filenames = await Promise.all(pdfPromises)

    console.log(`Generated ${filenames.length} PDFs:`, filenames)

    // Generate ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' })

    // Download ZIP file
    const zipFilename = `${safeCustomerName}_Consolidated_Statement_${startDate}_to_${endDate}.zip`
    saveAs(zipBlob, zipFilename)

    console.log(`Downloaded ZIP file: ${zipFilename}`)
  } catch (error) {
    console.error('Error generating multi-file PDFs:', error)
    throw new Error(`Failed to generate PDF files: ${error.message}`)
  }
}

/**
 * Get the expected number of PDF files that will be generated
 *
 * @param {Object} statementData - Consolidated statement data
 * @returns {number} Number of PDFs that will be generated
 */
export const getExpectedPDFCount = (statementData) => {
  return Object.keys(statementData.branchGroups).length
}
